import { lazy, Suspense, type FC, type ReactNode, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import dibsCss from 'dibs-css';
import { trackEvent } from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';

import { getHolidayShippingInfo } from './helpers/holidayDeliveryHelpers';
import {
    getHolidayShippingAbTestVariant,
    trackHolidayShippingAbTestVariant,
} from './helpers/holidayDeliveryAbTestHelpers';

// types
import { type PdpSharedShippingHolidayDelivery_sku$data as Sku } from './__generated__/PdpSharedShippingHolidayDelivery_sku.graphql';
import { type PdpSharedShippingHolidayDelivery_item$data as Item } from './__generated__/PdpSharedShippingHolidayDelivery_item.graphql';
import { type PdpSharedShippingHolidayDelivery_viewer$data as Viewer } from './__generated__/PdpSharedShippingHolidayDelivery_viewer.graphql';

// styles
import styles from './PdpSharedShippingHolidayDelivery.scss';

const PdpSharedShippingHolidayDeliveryModal = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryModal" */ './PdpSharedShippingHolidayDeliveryModal'
        )
);

const PdpSharedShippingHolidayDeliveryTooltip = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryTooltip" */ './PdpSharedShippingHolidayDeliveryTooltip'
        )
);

const trackHolidayDelivery = ({
    isFreeShipping,
    standardShipping,
}: {
    isFreeShipping: boolean;
    standardShipping: boolean;
}): void => {
    let shippingType = 'expedited';
    if (isFreeShipping) {
        shippingType = 'free';
    } else if (standardShipping) {
        shippingType = 'standard';
    }

    trackEvent({
        category: 'product',
        label: `${shippingType} shipping`,
        action: 'holiday get in time shipping displayed',
    });
};

type Props = {
    sku: Sku | null | undefined;
    item: Item;
    viewer: Viewer;
    isClient: boolean;
    useTooltipDetails?: boolean;
};

const PdpSharedShippingHolidayDeliveryComponent: FC<Props> = ({
    sku,
    item,
    viewer,
    isClient,
    useTooltipDetails,
}) => {
    const [hasFiredTracking, setHasFiredTracking] = useState<boolean>(false);
    const { holidayRelevantShipping: relevantShipping, transactionWarnings } = viewer;
    const { hasRestrictedMaterials, isCheckoutBlocked } = transactionWarnings || {};
    const shipmentDeliveryEstimations = sku
        ? sku?.holidayShippingEstimations
        : item?.holidayShippingEstimations;
    const {
        displayMessage,
        termsAndConditions = null,
        validHolidayShippingDays = null,
    } = shipmentDeliveryEstimations || {};

    const holidayShippingMethods = getHolidayShippingInfo({
        validHolidayShippingDays,
        shipmentQuotes: relevantShipping?.shipmentQuotes || [],
    });

    if (
        !holidayShippingMethods.length ||
        hasRestrictedMaterials ||
        isCheckoutBlocked ||
        !displayMessage
    ) {
        return null;
    }

    const variant = getHolidayShippingAbTestVariant();
    const isFirstVariant = variant === 'variant-1';

    const { totalAmount, isComplimentary, estimatedDeliveryDay, buyerServiceMethodDisplay } =
        holidayShippingMethods[0];

    const isFreeShipping = isComplimentary || totalAmount?.amount === 0;

    if (!hasFiredTracking && isClient) {
        if (variant) {
            trackHolidayShippingAbTestVariant();
        }

        if (!isFirstVariant) {
            trackHolidayDelivery({
                isFreeShipping,
                standardShipping: buyerServiceMethodDisplay === 'Standard',
            });
        }

        setHasFiredTracking(true);
    }

    if (isFirstVariant) {
        return null;
    }

    const isVariantTwo = variant === 'variant-2';

    return (
        <div className={styles.container}>
            <span className={styles.text}>
                <FormattedMessage
                    id="pdp.sharedHolidayShipping.estimatedDelivery.abTest.copy"
                    defaultMessage="<boldText>{displayMessage}:</boldText>{isFreeShipping, select,
                        true { Free}
                        other {}
                        } {isVariantTwo, select,
                            true {Arrives by <estimatedDeliveryDayWrapper>{estimatedDeliveryDay}{infoTooltip}</estimatedDeliveryDayWrapper> }
                            other {{buyerServiceMethodDisplay} Arrives by <estimatedDeliveryDayWrapper>{estimatedDeliveryDay}{infoTooltip}</estimatedDeliveryDayWrapper>}
                        }"
                    values={{
                        isVariantTwo,
                        // we want to ignore free shipping label in the second variant of the holiday shipping ab test
                        isFreeShipping: !isVariantTwo && isFreeShipping,
                        displayMessage,
                        buyerServiceMethodDisplay,
                        boldText: (chunks: ReactNode[]) => (
                            <span className={styles.boldText}>{chunks}</span>
                        ),
                        estimatedDeliveryDay,
                        estimatedDeliveryDayWrapper: content => (
                            <span className={dibsCss.whitespaceNowrap}>{content}</span>
                        ),
                        infoTooltip: useTooltipDetails && isClient && (
                            <span key="infoTooltipWrapper" className={dibsCss.whitespaceNormal}>
                                <Suspense fallback={null}>
                                    <PdpSharedShippingHolidayDeliveryTooltip
                                        termsAndConditions={termsAndConditions}
                                    />
                                </Suspense>
                            </span>
                        ),
                    }}
                />
                {!useTooltipDetails && isClient && (
                    <Suspense fallback={null}>
                        {' '}
                        <PdpSharedShippingHolidayDeliveryModal
                            termsAndConditions={termsAndConditions}
                        />
                    </Suspense>
                )}
            </span>
        </div>
    );
};

export const PdpSharedShippingHolidayDelivery = createFragmentContainer(
    PdpSharedShippingHolidayDeliveryComponent,
    {
        viewer: graphql`
            fragment PdpSharedShippingHolidayDelivery_viewer on Viewer {
                transactionWarnings(itemId: $itemId, country: $userCountryCode)
                    @include(if: $afterDisplay) {
                    hasRestrictedMaterials
                    isCheckoutBlocked
                }
                holidayRelevantShipping: relevantShipping(
                    itemId: $itemId
                    buyerId: $userId
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $afterDisplay) {
                    shipmentQuotes {
                        serviceId
                        isComplimentary
                        buyerServiceMethodDisplay
                        totalAmount {
                            amount
                        }
                        serviceMethod {
                            serviceType
                            serviceMethodType
                            region {
                                code
                            }
                        }
                    }
                }
            }
        `,
        item: graphql`
            fragment PdpSharedShippingHolidayDelivery_item on Item {
                holidayShippingEstimations(timezoneOffset: $timezoneOffset)
                    @include(if: $afterDisplay) {
                    displayMessage
                    termsAndConditions
                    validHolidayShippingDays {
                        shipmentQuoteId
                        estimatedDeliveryDay
                    }
                }
            }
        `,
        sku: graphql`
            fragment PdpSharedShippingHolidayDelivery_sku on ItemSkuType {
                holidayShippingEstimations(timezoneOffset: $timezoneOffset)
                    @include(if: $afterDisplay) {
                    displayMessage
                    termsAndConditions
                    validHolidayShippingDays {
                        shipmentQuoteId
                        estimatedDeliveryDay
                    }
                }
            }
        `,
    }
);
